import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings,
  LayoutDashboard,
  Inbox,
  MoreHorizontal,
} from 'lucide-react';
import './AdminDashboardShell.css';

const MOBILE_NAV = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'messages', label: 'Inbox', icon: Inbox },
];

const DEFAULT_PRODUCT = {
  mark: 'Q',
  title: 'QoreNova',
  subtitle: 'Command',
  role: 'Administrator',
  crumb: 'Admin',
  mobileEyebrow: 'QoreNova',
};

const AdminDashboardShell = ({
  adminInfo,
  activeTab,
  activeTabMeta,
  navGroups,
  subTabs = [],
  activeSubTab,
  onSubTabChange,
  onTabChange,
  sidebarCollapsed,
  onToggleCollapse,
  sidebarOpen,
  onToggleSidebar,
  isMobile,
  onLogout,
  onRefresh,
  loading,
  error,
  onDismissError,
  product = DEFAULT_PRODUCT,
  crumbIcon: CrumbIcon = Shield,
  mobileNav = MOBILE_NAV,
  children,
}) => {
  const activeLabel = activeTabMeta?.title || product.subtitle || 'Dashboard';

  return (
    <div className="adm-shell">
      <div className="adm-ambient" aria-hidden>
        <div className="adm-ambient__orb adm-ambient__orb--amber" />
        <div className="adm-ambient__orb adm-ambient__orb--violet" />
        <div className="adm-ambient__orb adm-ambient__orb--emerald" />
      </div>

      {isMobile && (
        <header className="adm-mobile-bar">
          <button
            type="button"
            className="adm-icon-btn adm-icon-btn--ghost"
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="adm-mobile-bar__center">
            <span className="adm-mobile-bar__eyebrow">{product.mobileEyebrow || product.title}</span>
            <span className="adm-mobile-bar__title">{activeLabel}</span>
          </div>
          <div className="adm-avatar adm-avatar--sm">{adminInfo.avatar}</div>
        </header>
      )}

      {isMobile && (
        <div
          className={`adm-overlay ${sidebarOpen ? 'adm-overlay--visible' : ''}`}
          onClick={onToggleSidebar}
          aria-hidden
        />
      )}

      <aside
        className={[
          'adm-sidebar',
          sidebarCollapsed && !isMobile ? 'adm-sidebar--collapsed' : '',
          isMobile ? (sidebarOpen ? 'adm-sidebar--open' : 'adm-sidebar--hidden') : '',
        ].filter(Boolean).join(' ')}
      >
        {!isMobile && (
          <div className="adm-sidebar__top">
            <Link to="/" className="adm-brand">
              <span className="adm-brand__mark">{product.mark}</span>
              {!sidebarCollapsed && (
                <span className="adm-brand__text">
                  <strong>{product.title}</strong>
                  <small>{product.subtitle}</small>
                </span>
              )}
            </Link>
            <button
              type="button"
              className="adm-icon-btn adm-icon-btn--ghost"
              onClick={onToggleCollapse}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        )}

        <div className={`adm-profile ${sidebarCollapsed && !isMobile ? 'adm-profile--collapsed' : ''}`}>
          <div className="adm-avatar">{adminInfo.avatar}</div>
          {(!sidebarCollapsed || isMobile) && (
            <div className="adm-profile__info">
              <div className="adm-profile__name">{adminInfo.name}</div>
              <div className="adm-profile__role">{product.role}</div>
            </div>
          )}
        </div>

        <nav className="adm-nav" aria-label="Admin navigation">
          {navGroups.map((group) => (
            <div key={group.label} className="adm-nav__group">
              {(!sidebarCollapsed || isMobile) && (
                <div className="adm-nav__label">{group.label}</div>
              )}
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={[
                    'adm-nav__item',
                    activeTab === item.id ? 'adm-nav__item--active' : '',
                    sidebarCollapsed && !isMobile ? 'adm-nav__item--collapsed' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onTabChange(item.id)}
                  title={item.label}
                >
                  <span className="adm-nav__icon">{item.icon}</span>
                  {(!sidebarCollapsed || isMobile) && (
                    <>
                      <span className="adm-nav__text">{item.label}</span>
                      {item.badge && (
                        <span className="adm-nav__pill adm-nav__pill--ai">{item.badge}</span>
                      )}
                      {item.count > 0 && !item.badge && (
                        <span className={`adm-nav__pill ${item.alert ? 'adm-nav__pill--alert' : ''}`}>
                          {item.count}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className={`adm-sidebar__footer ${sidebarCollapsed && !isMobile ? 'adm-sidebar__footer--collapsed' : ''}`}>
          <button
            type="button"
            className={`adm-footer-btn ${activeTab === 'settings' ? 'adm-footer-btn--active' : ''}`}
            onClick={() => onTabChange('settings')}
          >
            <Settings size={16} />
            {(!sidebarCollapsed || isMobile) && <span>Settings</span>}
          </button>
          <button type="button" className="adm-footer-btn adm-footer-btn--danger" onClick={onLogout}>
            <LogOut size={16} />
            {(!sidebarCollapsed || isMobile) && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div
        className={[
          'adm-main-wrap',
          sidebarCollapsed && !isMobile ? 'adm-main-wrap--collapsed' : '',
          isMobile ? 'adm-main-wrap--mobile' : '',
        ].filter(Boolean).join(' ')}
      >
        {!isMobile && (
          <header className="adm-topbar">
            <div className="adm-topbar__left">
              <div className="adm-breadcrumb">
                <CrumbIcon size={16} aria-hidden />
                <span>{product.crumb}</span>
                <ChevronRight size={14} aria-hidden />
                <strong>{activeLabel}</strong>
              </div>
              {activeTabMeta?.subtitle && (
                <p className="adm-topbar__subtitle">{activeTabMeta.subtitle}</p>
              )}
            </div>
            <div className="adm-topbar__actions">
              <button
                type="button"
                className="adm-icon-btn"
                onClick={onRefresh}
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw size={18} className={loading ? 'spin' : ''} />
              </button>
              <div className="adm-avatar adm-avatar--sm">{adminInfo.avatar}</div>
            </div>
          </header>
        )}

        {subTabs.length > 0 && (
          <nav className="adm-subnav" aria-label="Section navigation">
            {subTabs.map((subTab) => (
              <button
                key={subTab.id}
                type="button"
                className={`adm-subnav__btn ${activeSubTab === subTab.id ? 'adm-subnav__btn--active' : ''}`}
                onClick={() => onSubTabChange(subTab.id)}
              >
                {subTab.icon}
                <span>{subTab.label}</span>
                {subTab.count > 0 && (
                  <span className="adm-nav__pill adm-nav__pill--alert">{subTab.count}</span>
                )}
              </button>
            ))}
          </nav>
        )}

        {error && (
          <div className="adm-error-banner">
            <span>{error}</span>
            <button type="button" className="adm-icon-btn" onClick={onDismissError} aria-label="Dismiss">
              <X size={16} />
            </button>
          </div>
        )}

        <main className="adm-main">{children}</main>
      </div>

      {isMobile && (
        <nav className="adm-bottom-nav" aria-label="Quick navigation">
          {mobileNav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`adm-bottom-nav__item ${activeTab === item.id ? 'adm-bottom-nav__item--active' : ''}`}
                onClick={() => onTabChange(item.id)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={`adm-bottom-nav__item ${!mobileNav.some((n) => n.id === activeTab) ? 'adm-bottom-nav__item--active' : ''}`}
            onClick={onToggleSidebar}
          >
            <MoreHorizontal size={20} />
            <span>More</span>
          </button>
        </nav>
      )}
    </div>
  );
};

export default AdminDashboardShell;